const ARABIC_DAY_ORDINALS: Record<number, string> = {
  1: 'الأول',
  2: 'الثاني',
  3: 'الثالث',
  4: 'الرابع',
  5: 'الخامس',
  6: 'السادس',
  7: 'السابع',
  8: 'الثامن',
  9: 'التاسع',
  10: 'العاشر',
}

const ARABIC_ORDINAL_VALUES = new Set([
  ...Object.values(ARABIC_DAY_ORDINALS),
  'الاول',
])

const TATWEEL = 'ـــ'

function stretchAlWord(word: string) {
  if (!word.startsWith('ال') || word.length < 4) return word
  return `ال${TATWEEL}${word[2]}${TATWEEL}${word.slice(3)}`
}

const ARABIC_STRETCH_WORDS = [
  'اليوم',
  ...Object.values(ARABIC_DAY_ORDINALS).filter((word) => word !== 'الأول'),
]

const ARABIC_COLLAPSE_WORDS = [...ARABIC_STRETCH_WORDS, 'الأول', 'الاول']
const ARABIC_DAY_STRETCHES: Array<[string, string]> = ARABIC_STRETCH_WORDS.map((word) => [word, stretchAlWord(word)])
const ARABIC_DAY_COLLAPSES: Array<[string, string]> = ARABIC_COLLAPSE_WORDS.map((word) => [word, stretchAlWord(word)])

function normaliseDayNumber(dayNumber: number | string) {
  const parsed = typeof dayNumber === 'number' ? dayNumber : Number(String(dayNumber).trim())
  return Number.isFinite(parsed) ? parsed : null
}

function isArabicLocale(locale: string) {
  return locale?.toLowerCase().startsWith('ar')
}

const ARABIC_DAY_WORD = 'اليوم'

export function stretchArabicDayTitle(label: string) {
  const collapsed = ARABIC_DAY_COLLAPSES.reduce(
    (next, [plain, stretched]) => next.replaceAll(stretched, plain),
    label,
  )
  return ARABIC_DAY_STRETCHES.reduce(
    (next, [plain, stretched]) => next.replaceAll(plain, stretched),
    collapsed,
  )
}

function collapseArabicDayStretch(label: string) {
  return ARABIC_DAY_COLLAPSES.reduce(
    (next, [plain, stretched]) => next.replaceAll(stretched, plain),
    label,
  )
}

export function formatAgendaDayLabel(dayNumber: number | string, dayLabel: string, locale: string) {
  const n = normaliseDayNumber(dayNumber)
  if (n == null) return stretchArabicDayTitle(`${dayLabel} ${dayNumber}`)

  if (isArabicLocale(locale)) {
    const word = dayLabel.trim() === ARABIC_DAY_WORD || !dayLabel.trim() ? ARABIC_DAY_WORD : dayLabel.trim()
    const ordinal = ARABIC_DAY_ORDINALS[n]
    if (ordinal) return stretchArabicDayTitle(`${word} ${ordinal}`)
    return stretchArabicDayTitle(`${word} ${n}`)
  }

  const word = dayLabel.trim() === ARABIC_DAY_WORD || !dayLabel.trim() ? 'Day' : dayLabel.trim()
  return `${word} ${n}`
}

export function resolveAgendaDayLabel(
  dayNumber: number | string,
  dayLabel: string,
  locale: string,
  backendLabel?: string | null,
) {
  // Always re-localize standard Day N / اليوم … labels so switching EN↔AR works.
  if (!backendLabel || isGenericDayFallback(backendLabel, dayNumber)) {
    return formatAgendaDayLabel(dayNumber, dayLabel, locale)
  }

  return isArabicLocale(locale) ? stretchArabicDayTitle(backendLabel) : collapseArabicDayStretch(backendLabel)
}

function isGenericDayFallback(label: string, dayNumber: number | string) {
  const n = normaliseDayNumber(dayNumber)
  const normalized = collapseArabicDayStretch(label).trim().replace(/\s+/g, ' ')

  if (/^Day\s+\d+$/i.test(normalized)) return true
  if (/^اليوم\s+[\d٠-٩]+$/u.test(normalized)) return true
  if (n != null && (normalized === `Day ${n}` || normalized === `اليوم ${n}`)) return true

  const arabicMatch = normalized.match(/^اليوم\s+(.+)$/u)
  if (arabicMatch && ARABIC_ORDINAL_VALUES.has(arabicMatch[1].trim())) return true

  if (n != null) {
    const expectedOrdinal = ARABIC_DAY_ORDINALS[n]
    if (expectedOrdinal && normalized === `اليوم ${expectedOrdinal}`) return true
  }

  return false
}
